/**
 * Ledger statement endpoints (bos-api).
 *
 * Paths are relative to the axios client's base, which already ends in `/api/v1`
 * — so `/ledger/statements/balance-sheet` reaches
 * `…/api/v1/ledger/statements/balance-sheet`. This mirrors `AccountsEndpoints`
 * and `JournalEndpoints`, the other live ledger resources.
 *
 * Two things the client originally assumed and got wrong, both silent failures:
 *
 * - **The P&L range is `dateFrom`/`dateTo`**, not `fromDate`/`toDate` — the same
 *   trap the journal list hit. The server ignores unknown query parameters, so
 *   the old names produced a default-window statement that looked like the
 *   requested one.
 * - **The paths live under `/ledger/statements`**, not `/statements`.
 *
 * `comparative` and `locale` are what make one request enough: the comparative
 * column, its window, and every label come back in the same payload.
 *
 * `PUT /ledger/statements/layout/lines` and `.../layout/reorder` exist on the API
 * and are deliberately absent here. Re-labelling or re-ordering a statement
 * changes the reporting structure of the chart, not the report being viewed, and
 * these screens are read-only. Not having the URLs here keeps them unreachable
 * from a statement page.
 */

/** Query options shared by both statements. */
type CommonStatementQuery = {
    /** Which locale the server should resolve every `label` into. */
    locale: string;
    /** `NONE` or `PRIOR_YEAR`. Returned in the same call. */
    comparative: 'NONE' | 'PRIOR_YEAR';
    /**
     * Keep lines whose value is nil.
     *
     * Always `true` from these screens. The server suppresses nil lines by
     * default, which makes a statement change shape from period to period — a
     * historical as-of with no activity collapses to two total rows (measured:
     * 2 lines instead of 22). An accountant reads a statement by comparing the
     * same lines in the same order every period, so the client asks for the
     * complete layout and de-emphasises the zeros itself. Recorded in
     * `src/pages/accounting/README.md`.
     */
    includeZeroLines: boolean;
};

export type BalanceSheetQuery = CommonStatementQuery & {
    /** ISO `YYYY-MM-DD`, inclusive. */
    asOf: string;
};

export type ProfitAndLossQuery = CommonStatementQuery & {
    /** ISO `YYYY-MM-DD`, inclusive. */
    dateFrom: string;
    /** ISO `YYYY-MM-DD`, inclusive. */
    dateTo: string;
};

export type PlausibilityQuery = {
    asOf: string;
    locale: string;
};

const search = (entries: Record<string, string>): string => {
    const query = new URLSearchParams(entries).toString();
    return query ? `?${query}` : '';
};

export const StatementsEndpoints = {
    /** GET — the position as of a date, with its comparative in one payload. */
    balanceSheet: ({
        asOf,
        locale,
        comparative,
        includeZeroLines,
    }: BalanceSheetQuery) =>
        `/ledger/statements/balance-sheet${search({
            asOf,
            locale,
            comparative,
            includeZeroLines: String(includeZeroLines),
        })}`,

    /** GET — the result over a range, with its comparative in one payload. */
    profitAndLoss: ({
        dateFrom,
        dateTo,
        locale,
        comparative,
        includeZeroLines,
    }: ProfitAndLossQuery) =>
        `/ledger/statements/profit-and-loss${search({
            dateFrom,
            dateTo,
            locale,
            comparative,
            includeZeroLines: String(includeZeroLines),
        })}`,

    /**
     * GET — advisory plausibility notices, including `ASSETS_NE_EQLIAB` (the
     * Balance Sheet not balancing).
     *
     * A second request per statement view. `StatementResponse` has no warnings
     * field, so there is no way to get these alongside the statement — and a
     * consumer that forgets to ask renders a silently unbalanced Balance Sheet as
     * though it were fine. Flagged in `src/pages/accounting/README.md`.
     */
    plausibility: ({ asOf, locale }: PlausibilityQuery) =>
        `/ledger/statements/plausibility${search({ asOf, locale })}`,

    /**
     * GET — the raw layout tree (parent links, `computedKey`, `accountCodes`, and
     * `label` as a **locale map** rather than resolved).
     *
     * Not used by these screens: a rendered statement already carries its own
     * labels, depth and order, so fetching the layout as well would be a second
     * source for the same structure. Recorded so the path lives in one place.
     */
    layout: (statement: 'BALANCE_SHEET' | 'PROFIT_LOSS') =>
        `/ledger/statements/layout${search({ statement })}`,
};
